'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { DefaultXRController, PointerCursorModel, PointerRayModel, XRSpace, defaultRayPointerOpacity, useRayPointer, useXR, useXRInputSourceStateContext, } from '@react-three/xr';
import { applyDeadZone, isPressed } from '../_pure/spaceCanvasHelpers.js';
import { spaceCanvasXrBindings } from '../xrBindings.js';
import { XrControllerHud } from './XrBindings.js';
export default function SpaceCanvasXrLeftController() {
    const controller = useXRInputSourceStateContext('controller');
    const session = useXR(state => state.session);
    const space = useRef(null);
    const pointer = useRayPointer(space, controller, { makeDefault: true, minDistance: 0.2 });
    const cancelSelection = useRef(null);
    useEffect(() => {
        if (!session)
            return;
        let selecting = false;
        // Read live input here: select events can arrive before the mapped gamepad's frame update.
        // A held shortcut (such as push-to-talk) owns the trigger too, so it must not select.
        const gripHeld = () => spaceCanvasXrBindings.capturesTrigger('left')
            || applyDeadZone(controller.inputSource.gamepad?.buttons[1]?.value
                ?? controller.gamepad['xr-standard-squeeze']?.button
                ?? (isPressed(controller.gamepad['xr-standard-squeeze']) ? 1 : 0)) > 0;
        const cancel = (timeStamp) => {
            if (!selecting)
                return;
            selecting = false;
            const event = { timeStamp, button: 0 };
            pointer.cancel(event);
            // Clear the held pointer button without generating pointerup/click.
            pointer.setEnabled(false, event);
            pointer.up(event);
            pointer.exit(event);
            pointer.setEnabled(true, event);
        };
        cancelSelection.current = timeStamp => {
            if (gripHeld())
                cancel(timeStamp);
        };
        const down = (event) => {
            if (event.inputSource !== controller.inputSource || gripHeld())
                return;
            selecting = true;
            pointer.down(Object.assign(event, { button: 0 }));
        };
        const up = (event) => {
            if (event.inputSource !== controller.inputSource || !selecting)
                return;
            if (gripHeld()) {
                cancel(event.timeStamp);
                return;
            }
            selecting = false;
            pointer.up(Object.assign(event, { button: 0 }));
        };
        session.addEventListener('selectstart', down);
        session.addEventListener('selectend', up);
        for (const event of controller.events) {
            if (event.type === 'selectstart')
                down(event);
            if (event.type === 'selectend')
                up(event);
        }
        return () => {
            session.removeEventListener('selectstart', down);
            session.removeEventListener('selectend', up);
            cancelSelection.current = null;
            cancel(performance.now());
        };
    }, [controller, pointer, session]);
    useFrame(() => cancelSelection.current?.(performance.now()));
    return _jsxs(_Fragment, { children: [_jsx(DefaultXRController, { grabPointer: false, rayPointer: false }), _jsx(XrControllerHud, { hand: "left" }), _jsxs(XRSpace, { ref: space, space: "target-ray-space", children: [_jsx(PointerRayModel, { pointer: pointer, opacity: defaultRayPointerOpacity }), _jsx(PointerCursorModel, { pointer: pointer, opacity: defaultRayPointerOpacity })] })] });
}
